import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { ordersApi } from '../api/ordersApi'
import { notify } from '@/shared/utils'
import { requestNotificationPermission, showBrowserNotification } from '@/shared/utils/browserNotification'

export function useOrderPolling(vendorId: string, enabled = true) {
  const queryClient = useQueryClient()
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Request browser notification permission on mount
  useEffect(() => {
    requestNotificationPermission()
  }, [])

  useEffect(() => {
    if (!vendorId || !enabled) return

    const poll = async () => {
      try {
        const result = await ordersApi.pollNewOrders(vendorId)
        if (result?.count > 0) {
          try {
            const audio = new Audio('/notification.mp3')
            audio.play().catch(() => {})
          } catch {}

          notify.success(`${result.count} new order${result.count > 1 ? 's' : ''} received!`)
          showBrowserNotification(
            'New Order Received',
            `${result.count} new order${result.count > 1 ? 's' : ''} received!`
          )
          queryClient.invalidateQueries({ queryKey: ['vendor-orders'] })
        }
      } catch {
        // Silently fail poll
      }
    }

    intervalRef.current = setInterval(poll, 12000)

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [vendorId, enabled, queryClient])

  // Refetch orders when tab becomes visible again
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        queryClient.invalidateQueries({ queryKey: ['vendor-orders'] })
      }
    }
    document.addEventListener('visibilitychange', handleVisibility)
    return () => document.removeEventListener('visibilitychange', handleVisibility)
  }, [queryClient])
}
